<template>
  <div>
    <!-- Self view: set or update own PIN -->
    <template v-if="isSelf">
      <UiBaseFormVee
        submitOnCtrlEnter
        :errorText="errorText"
        :onSubmit="onSubmit">
        <!-- Current PIN (only shown when one is already set) -->
        <div
          v-if="user.options.hasPinCode"
          class="mb-4">
          <span class="mb-1 block text-sm font-medium">
            {{ $t('W: Nuværende pinkode') }}
          </span>
          <PinInput
            v-model="form.currentPin"
            :isError="isCurrentPinError"
            @update:model-value="onPinInput" />
        </div>

        <div class="mb-4">
          <span class="mb-1 block text-sm font-medium">
            {{ $t('W: Ny pinkode') }}
          </span>
          <PinInput
            v-model="form.newPin"
            :isError="isNewPinError"
            @update:model-value="onPinInput" />
        </div>

        <div class="mb-4">
          <span class="mb-1 block text-sm font-medium">
            {{ $t('W: Bekræft ny pinkode') }}
          </span>
          <PinInput
            v-model="form.confirmPin"
            :isError="isConfirmPinError"
            @update:model-value="onPinInput" />
        </div>

        <template #buttons>
          <UiBaseButton
            :text="
              user.options.hasPinCode
                ? $t('W: Opdater pinkode')
                : $t('W: Opret pinkode')
            "
            :isLoading="isSaving"
            type="submit" />
        </template>
      </UiBaseFormVee>
    </template>

    <!-- Admin/owner view: reset another user's PIN -->
    <template v-else>
      <div
        v-if="user.options.hasPinCode"
        class="flex flex-col gap-4">
        <span class="text-sm text-text-primary">
          {{ $t('W: Denne medarbejder har en pinkode sat.') }}
        </span>
        <div>
          <UiBaseButton
            theme="neutral"
            :text="$t('W: Nulstil pinkode')"
            :isLoading="isResetting"
            @button-click="onResetClick" />
        </div>
        <InfoText
          v-if="errorText"
          :text="errorText"
          theme="alert" />
      </div>
      <div v-else>
        <span class="text-sm text-text-primary">
          {{ $t('W: Denne medarbejder har ingen pinkode.') }}
        </span>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import type { LabManagementUser } from '@/globals/typescript/models/LabManagementUser'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useToast } from '@/globals/typescript/utils/toast'
import { useModalStore } from '@/stores/modalStore'
import { useUserStore } from '@/stores/userStore'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'

export default {
  name: 'EmployeeEditPinCodeView',
  props: {
    user: {
      type: Object as PropType<LabManagementUser>,
      required: true,
    },
  },
  data() {
    return {
      form: {
        currentPin: '',
        newPin: '',
        confirmPin: '',
      },

      isSaving: false,
      isResetting: false,
      errorText: '',
      isCurrentPinError: false,
      isNewPinError: false,
      isConfirmPinError: false,
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser']),
    isSelf(): boolean {
      return this.user.id === this.currentUser?.id
    },
  },
  watch: {
    user() {
      this.resetForm()
    },
  },
  methods: {
    ...mapActions(useModalStore, ['open']),
    onPinInput() {
      this.errorText = ''
      this.isCurrentPinError = false
      this.isNewPinError = false
      this.isConfirmPinError = false
    },
    resetForm() {
      this.form = { currentPin: '', newPin: '', confirmPin: '' }
      this.errorText = ''
      this.isCurrentPinError = false
      this.isNewPinError = false
      this.isConfirmPinError = false
    },
    async onSubmit() {
      this.errorText = ''
      this.isCurrentPinError = false
      this.isNewPinError = false
      this.isConfirmPinError = false

      if (this.user.options.hasPinCode && this.form.currentPin.length < 4) {
        this.errorText = this.$t('W: Den nuværende pinkode skal være 4 cifre')
        this.isCurrentPinError = true
        return
      }

      if (this.form.newPin !== this.form.confirmPin) {
        this.errorText = this.$t('W: Pinkoderne stemmer ikke overens')
        this.isNewPinError = true
        this.isConfirmPinError = true
        return
      }

      if (this.form.newPin.length !== 4) {
        this.errorText = this.$t('W: Pinkoden skal være 4 cifre')
        this.isNewPinError = true
        return
      }

      this.isSaving = true

      try {
        let result

        if (this.user.options.hasPinCode) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result = await managementApi('patch', '/users/me/pin' as any, {
            body: {
              currentPin: this.form.currentPin,
              newPin: this.form.newPin,
              confirmPin: this.form.confirmPin,
            },
          })
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result = await managementApi('post', '/users/me/pin' as any, {
            body: {
              newPin: this.form.newPin,
              confirmPin: this.form.confirmPin,
            },
          })
        }

        if (result.success) {
          const successMsg = this.user.options.hasPinCode
            ? this.$t('W: Pinkode opdateret')
            : this.$t('W: Pinkode oprettet')
          useToast().success(successMsg)
          this.resetForm()
          return
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((result as any).code === 'invalid-credentials') {
          this.errorText = this.$t('W: Den nuværende pinkode er forkert')
          this.isCurrentPinError = true
          return
        }

        useToast().error(this.$t('W: Kunne ikke gemme pinkode'))
        this.errorText = this.$t('error.common.standard')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isSaving = false
      }
    },
    onResetClick() {
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Nulstil pinkode'),
          textLines: [
            this.$t(
              'W: Er du sikker på, at du vil nulstille pinkoden for denne medarbejder?'
            ),
          ],
          onConfirm: () => this.doReset(),
        },
      })
    },
    async doReset() {
      this.isResetting = true
      this.errorText = ''
      try {
        await managementApi('delete', '/users/{userID}/pin', {
          path: { userID: this.user.id },
        })
        useToast().success(this.$t('W: Pinkode nulstillet'))
      } catch {
        useToast().error(this.$t('W: Kunne ikke nulstille pinkode'))
        this.errorText = this.$t('error.common.standard')
      } finally {
        this.isResetting = false
      }
    },
  },
}
</script>

